import React from 'react';
import { ArrowRight, Shield, Globe, TrendingUp, CheckCircle, Zap } from 'lucide-react';

interface HeroSectionProps {
  onGetStartedClick?: () => void;
  onLearnMoreClick?: () => void;
  onSignupClick?: () => void;
}

const trustItems = [
  { icon: <Shield size={14} />, label: 'Bank-grade security' },
  { icon: <Globe size={14} />, label: '50+ countries' },
  { icon: <TrendingUp size={14} />, label: 'Real-time rates' },
  { icon: <Zap size={14} />, label: '99.9% uptime' },
];

const HeroSection: React.FC<HeroSectionProps> = ({ onGetStartedClick, onLearnMoreClick }) => {
  return (
    <section className="relative overflow-hidden min-h-[88vh] flex items-center border-y border-white/20">
      <div className="absolute inset-0 bg-gradient-to-br from-fintech-primary/82 via-blue-800/76 to-slate-900/72 backdrop-blur-[1px]" />
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-blue-400/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-blue-700/10 blur-3xl" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
        {/* Diagonal accent line */}
        <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-white/5 to-transparent" style={{ right: '25%' }} />
      </div>

      <div className="section-container relative z-10 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto text-center public-readable-panel public-readable-panel--dark">
          {/* Eyebrow badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-blue-100 text-sm font-medium mb-8 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-fintech-accent animate-pulse" />
            Procurement as a Service Platform
          </div>

          {/* Main headline */}
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 tracking-tight">
            The Smarter Way to{' '}
            <span className="relative">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-cyan-200">
                Procure Globally
              </span>
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-blue-200 leading-relaxed mb-10 max-w-2xl mx-auto">
            Onboard in minutes. Pay vendors across 50+ countries with confidence.
            Enterprise-grade procurement infrastructure built for modern businesses.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <button
              className="btn-primary-large bg-white text-fintech-primary hover:bg-blue-50 w-full sm:w-auto"
              onClick={onGetStartedClick}
            >
              Get Started Free
              <ArrowRight size={18} />
            </button>
            <button
              className="btn-secondary-large border-white/30 text-white hover:bg-white/10 w-full sm:w-auto"
              style={{ backgroundColor: 'transparent', borderColor: 'rgba(255,255,255,0.3)' }}
              onClick={onLearnMoreClick}
            >
              Learn More
            </button>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            {trustItems.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-blue-200 text-sm">
                <span className="text-fintech-accent">{item.icon}</span>
                {item.label}
              </div>
            ))}
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-12 lg:mt-16 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-3xl mx-auto public-readable-panel public-readable-panel--dark public-readable-panel--wide">
          {[
            { value: '$60M+', label: 'All time processed', color: 'text-emerald-300' },
            { value: '500+', label: 'Active businesses', color: 'text-blue-200' },
            { value: '50+', label: 'Countries supported', color: 'text-cyan-300' },
            { value: '99.9%', label: 'Uptime SLA', color: 'text-amber-300' },
          ].map((stat, i) => (
            <div
              key={i}
              className="text-center p-5 rounded-2xl border border-white/10 backdrop-blur-sm"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
            >
              <p className={`text-2xl font-bold mb-1 ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-blue-300/80">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Social proof strip */}
        <div className="mt-10 flex items-center justify-center gap-2 public-readable-panel public-readable-panel--dark public-readable-panel--compact">
          <div className="flex -space-x-2">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-7 h-7 rounded-full border-2 border-blue-800 flex items-center justify-center text-xs font-bold text-white"
                style={{ backgroundColor: `hsl(${210 + i * 15}, 70%, ${40 + i * 5}%)` }}
              >
                {String.fromCharCode(65 + i)}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-1.5 text-blue-200 text-sm">
            <CheckCircle size={14} className="text-fintech-accent" />
            <span>Trusted by 500+ procurement teams</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
